import React, { useCallback, useEffect, useRef, useState } from 'react'
import { NotificationItem } from './Notifications.styles'
import Skeleton from '@mui/material/Skeleton'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import { useSetHeaderTitle } from '../../../hooks/hooks'
import MobileHeader from '../../../components/MobileHeader/MobileHeader'
import { useFetchNotificationsQuery, useMarkAllNotificationAsSeenMutation } from '../../../services/api'
import DefaultNotificationView from './components/DefaultNotificationView'
import Text from '../../../components/Text'
import { PageWrapper, colors } from '../../../styles/_var'
import { groupByDay, unslugify } from '../../../utils/helpers'
import { Notification } from '../../../utils/types'

export const NotificationSkeleton = () => (
  <Box>
    {(new Array(2)).fill(true).map((_, i) => (
      <NotificationItem key={i}>
        <Box display="flex" justifyContent="center" flex={1}>
          <Skeleton variant="circular" height={48} width={48} />
          <Skeleton variant="text" sx={{ml: 1, mr: 2, width: "80%"}} />
        </Box>
        <Skeleton variant="text" sx={{width: 30}} />
      </NotificationItem>
    ))}
  </Box>
)

type MarkAsSeenProps = {
  markAllAsRead: () => void
}

const MarkAsSeen = ({markAllAsRead}: MarkAsSeenProps) => {
  return (
    <Box justifyContent="flex-end" display="flex" mt={2} mr={2}>
      <Text 
        link 
        underline 
        onClick={markAllAsRead} 
        textDecorationStyle="dotted"
      >
        Mark All As Read
      </Text>
    </Box>
  )
}


const Notifications = () => {
  useSetHeaderTitle('Notifications', true)
  const [page, setPage] = useState(1)
  const loadMoreRef = useRef<HTMLDivElement | null>(null)
  const { data, isSuccess, isLoading, isFetching } = useFetchNotificationsQuery(
    { page },
    { refetchOnMountOrArgChange: true }
  )
  const [markAllAsRead,] = useMarkAllNotificationAsSeenMutation()
  const notifications = data?.payload ?? []
  const groupedByDate =
    notifications.length > 0
      ? groupByDay<Notification>(notifications, 'created_at')
      : null
  const nextPage = data?.pagination_info?.next_page
  const hasMore = !!nextPage && !data?.pagination_info?.last_page
  const loadingMore = isFetching && page > 1

  const loadMore = useCallback(() => {
    if (!hasMore || isFetching || !nextPage) return
    setPage(nextPage)
  }, [hasMore, isFetching, nextPage])

  useEffect(() => {
    const node = loadMoreRef.current
    if (!node) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          loadMore()
        }
      },
      { root: null, rootMargin: '160px', threshold: 0 }
    )

    observer.observe(node)
    return () => observer.disconnect()
  }, [loadMore, groupedByDate])

  return (
    <PageWrapper>
      <MobileHeader />
      <MarkAsSeen markAllAsRead={() => markAllAsRead({})} />
      {isLoading && page === 1 && <NotificationSkeleton />}
      {(isSuccess && !notifications.length) && (
        <Text mt={2} color={colors.danger} center weight="semibold">You have no notifications.</Text>
      )}
      {!!groupedByDate && Object.keys(groupedByDate).map((date, i) => (
        <Box key={i} pt={1}>
          <Text ml={2} mb={1} weight="semibold" capitalize>
            {unslugify(date)}
          </Text>
          {groupedByDate[date].map((n) => (
            <DefaultNotificationView key={n.id} notification={n} />
          ))}
        </Box>
      ))}
      {!!groupedByDate && (
        <Box ref={loadMoreRef} display="flex" justifyContent="center" py={2}>
          {loadingMore ? <CircularProgress size={28} color="info" /> : null}
        </Box>
      )}
    </PageWrapper>
  )
}

export default Notifications
